/**
 * Stale-bundle detector for the installed PWA.
 *
 * The problem: `public/sw.js` serves navigations network-first, so a genuine page
 * *reload* always picks up a deploy's fresh chunk hashes. But an installed PWA
 * (iOS especially) usually RESUMES its existing page on relaunch rather than
 * navigating — so `skipWaiting()` + `clients.claim()` activate the new worker while
 * the tab keeps running the old JS indefinitely. "I restarted the app" does not
 * mean "I got the new build", which is exactly how a shipped fix can look unshipped.
 *
 * The fix: `writeVersionJsonPlugin` (vite.config.ts) already emits `/version.json`
 * at build time, and nothing has ever read it. This polls it and compares against the
 * version compiled into the running bundle. When they differ, a new deploy exists and
 * this tab is stale.
 *
 * We prompt rather than auto-reload: a silent reload mid-message would lose whatever
 * the user was typing. The toast is sticky and dismissible, and has a handle to drag it
 * out of the way (UpdateToast); where it was dragged to is not remembered.
 */
import { createElement } from 'react';
import { toast } from 'sonner';
import { UpdateToast } from '@/components/shared/UpdateToast';

/** Version compiled into THIS bundle. Must match the `buster` in main.tsx. */
const RUNNING_VERSION = import.meta.env.VITE_BUILD_VERSION || 'v1';

/** Poll cadence while the tab is visible. */
const POLL_MS = 5 * 60_000;

let notified = false;
let timer: ReturnType<typeof setInterval> | null = null;

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    // `no-store` bypasses both the HTTP cache and the service worker's caches —
    // version.json is not under /assets/, so it is never cache-first anyway, but a
    // stale CDN copy would defeat the whole check.
    const res = await fetch(`/version.json?t=${performance.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as { version?: string };
    return body.version ?? null;
  } catch {
    // Offline, or version.json not deployed (dev). Never surface this.
    return null;
  }
}

/** Reload onto the new build, pulling the newest worker first. */
export async function applyUpdate(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    await reg?.update();
  } catch {
    // A failed worker update must not block the reload — the navigation is
    // network-first, so the fresh HTML (and its chunk hashes) arrive regardless.
  }
  window.location.reload();
}

async function check(): Promise<void> {
  if (notified) return;
  const deployed = await fetchDeployedVersion();
  if (!deployed || deployed === RUNNING_VERSION) return;

  notified = true;
  toast.custom((id) => createElement(UpdateToast, { id, onReload: () => void applyUpdate() }), {
    duration: Infinity,
    // Off so a drag on the handle moves the toast instead of swiping it away; the toast's
    // own X dismisses it (toast.dismiss still runs onDismiss below).
    dismissible: false,
    onDismiss: () => {
      // Allow a later re-prompt if they dismiss and keep the app open.
      notified = false;
    },
  });
}

/**
 * Start watching for new deploys. Checks whenever the app returns to the foreground
 * (the PWA-relaunch path that misses a navigation) and on a slow poll.
 *
 * Startup cost is deliberately zero: the first check is deferred to idle time after
 * the page has loaded, so it never competes with the initial render or the first data
 * fetches. A stale bundle is a minutes-scale problem, not a milliseconds-scale one.
 * Disabled entirely in dev, where /version.json doesn't exist.
 */
export function startUpdateChecker(): () => void {
  if (import.meta.env.DEV) return () => {};

  const ric = window.requestIdleCallback as
    | ((cb: () => void, opts?: { timeout: number }) => number)
    | undefined;
  const idle = (fn: () => void) =>
    ric ? ric(fn, { timeout: 10_000 }) : window.setTimeout(fn, 3_000);

  // Wait for `load` (all critical resources fetched), then for the main thread to
  // actually be idle, before touching the network.
  if (document.readyState === 'complete') idle(() => void check());
  else window.addEventListener('load', () => idle(() => void check()), { once: true });

  const onVisible = () => {
    if (document.visibilityState === 'visible') void check();
  };
  document.addEventListener('visibilitychange', onVisible);
  timer = setInterval(() => {
    if (document.visibilityState === 'visible') void check();
  }, POLL_MS);

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    if (timer) clearInterval(timer);
  };
}
